import { useCallback, useEffect, useRef, useState } from "react";

// Turn-by-turn voice engine. A future live engine (OpenAI Realtime) only needs the same
// { state, level, listen, stopListening, interrupt, play, supported } shape — the kiosk UI stays unchanged.
export const VOICE_ENGINE = "turn_based";
const END_SILENCE_MS = 900;
const MIN_SPEECH_MS = 350;      // shorter bursts (cutlery, a cough, a door) are ignored
const TAP_MIN_SPEECH_MS = 140;  // Tap to Speak: the customer chose to talk, so short answers ("medium", "yes") count
const MAX_TURN_MS = 20000;
const NO_SPEECH_MS = 8000;
const ECHO_TAIL_MS = 450;       // wait after the AI stops talking before listening again

// sensitivity 1 (least sensitive, noisy café) … 10 (most sensitive, quiet room)
export const speechThreshold = (sens = 5) => 0.11 - (Math.max(1, Math.min(10, sens)) - 1) * 0.0105;

export function micConstraints(micId) {
  const sup = navigator.mediaDevices?.getSupportedConstraints?.() || {};
  const a = { channelCount: 1 };
  ["echoCancellation", "noiseSuppression", "autoGainControl"].forEach((k) => { if (sup[k]) a[k] = true; });
  if (sup.voiceIsolation) a.voiceIsolation = true;
  if (micId) a.deviceId = { exact: micId };
  return { audio: a };
}

// What the browser ACTUALLY applied (never assume a requested setting is active).
export function appliedAudio(stream) {
  const t = stream?.getAudioTracks?.()[0];
  const s = t?.getSettings?.() || {};
  return { label: t?.label || "Microphone", echoCancellation: s.echoCancellation, noiseSuppression: s.noiseSuppression,
    autoGainControl: s.autoGainControl, voiceIsolation: s.voiceIsolation };
}

function pickMime() {
  const c = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
  return c.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
}

export function useTurnEngine({ onUtterance, onActivity, sensitivity = 5, autoBarge = false, micId = "", tapMode = true }) {
  // Tap to Speak: never stricter than a medium setting, so a one-word answer isn't treated as silence.
  sensitivity = tapMode ? Math.max(sensitivity, 6) : sensitivity;
  const minSpeech = tapMode ? TAP_MIN_SPEECH_MS : MIN_SPEECH_MS;
  const [state, setState] = useState("idle"); // idle | listening | speaking
  const [level, setLevel] = useState(0);
  const [micError, setMicError] = useState("");
  const [applied, setApplied] = useState(null);
  const r = useRef({ floor: 0.01 });
  const supported = !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);

  const ensureMic = useCallback(async () => {
    if (r.current.stream) return r.current.stream;
    try {
      const stream = await navigator.mediaDevices.getUserMedia(micConstraints(micId));
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const hp = ctx.createBiquadFilter(); // cut rumble (fridges, coffee grinder hum) below the voice band
      hp.type = "highpass"; hp.frequency.value = 120;
      const an = ctx.createAnalyser();
      an.fftSize = 1024;
      src.connect(hp); hp.connect(an);
      Object.assign(r.current, { stream, ctx, an, buf: new Float32Array(an.fftSize) });
      setApplied(appliedAudio(stream)); setMicError("");
      return stream;
    } catch (e) {
      setMicError(e?.name === "NotAllowedError" ? "Microphone access is blocked — allow it in the browser's site settings, or order on the screen."
        : e?.name === "NotFoundError" ? "No microphone found — plug one in or order on the screen." : "Microphone unavailable — please order on the screen.");
      throw e;
    }
  }, [micId]);

  const rms = () => {
    const { an, buf } = r.current;
    if (!an) return 0;
    an.getFloatTimeDomainData(buf);
    let s = 0;
    for (let i = 0; i < buf.length; i += 1) s += buf[i] * buf[i];
    return Math.sqrt(s / buf.length);
  };
  // Adaptive floor: speech must beat the café's current background level, not just a fixed number.
  const isVoice = (v, mult = 2.2) => v > Math.max(speechThreshold(sensitivity), r.current.floor * mult);
  const learnFloor = (v) => { r.current.floor = r.current.floor * 0.97 + Math.min(v, 0.2) * 0.03; };
  const stopLoop = () => { cancelAnimationFrame(r.current.raf); r.current.raf = null; };

  const stopListening = useCallback((send = true) => {
    const mr = r.current.mr;
    r.current.send = send;
    if (mr && mr.state === "recording") mr.stop();
    stopLoop();
  }, []);

  const listen = useCallback(async () => {
    if (r.current.mr?.state === "recording") return; // never two overlapping recordings
    const playing = r.current.audio;
    if (playing) { playing.pause(); playing.src = ""; r.current.audio = null; r.current.resolvePlay?.("interrupted"); stopLoop(); } // never record the AI's own voice
    await ensureMic();
    if (r.current.mr?.state === "recording") return;
    if (r.current.ctx?.state === "suspended") await r.current.ctx.resume();
    const mime = pickMime();
    const mr = new MediaRecorder(r.current.stream, mime ? { mimeType: mime } : undefined);
    let chunks = []; // fresh buffer for THIS utterance only
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = () => {
      setState("idle"); setLevel(0);
      const take = chunks; chunks = [];
      if (r.current.mr === mr) r.current.mr = null;
      if (!r.current.send || !r.current.heard || !take.length) { r.current.onNoSpeech?.(); return; }
      const type = mr.mimeType || mime || "audio/webm";
      onUtterance(new Blob(take, { type }), type.includes("mp4") || type.includes("aac") ? "mp4" : "webm");
    };
    Object.assign(r.current, { mr, heard: false, voicedMs: 0, send: true, started: Date.now(), lastVoice: 0, lastTick: Date.now() });
    mr.start(250);
    setState("listening");
    const tick = () => {
      const v = rms();
      const now = Date.now();
      const dt = now - r.current.lastTick;
      r.current.lastTick = now;
      setLevel(v);
      if (isVoice(v)) {
        r.current.voicedMs += dt; r.current.lastVoice = now;
        if (r.current.voicedMs >= minSpeech && !r.current.heard) { r.current.heard = true; onActivity?.(); }
      } else {
        learnFloor(v);
        if (!r.current.heard && now - r.current.lastVoice > 250) r.current.voicedMs = 0; // burst ended before it counted
      }
      const { heard, lastVoice, started } = r.current;
      if ((heard && now - lastVoice > END_SILENCE_MS) || now - started > MAX_TURN_MS || (!heard && now - started > NO_SPEECH_MS)) {
        stopListening(true);
        return;
      }
      r.current.raf = requestAnimationFrame(tick);
    };
    r.current.raf = requestAnimationFrame(tick);
  }, [ensureMic, onUtterance, onActivity, stopListening, sensitivity]); // eslint-disable-line react-hooks/exhaustive-deps

  const interrupt = useCallback(() => {
    const a = r.current.audio;
    if (a) { a.pause(); a.src = ""; r.current.audio = null; r.current.resolvePlay?.("interrupted"); }
    stopLoop();
    setState("idle");
  }, []);

  // Plays the AI reply. Spoken barge-in only when the owner enabled it (strict threshold, sustained speech);
  // tapping the mic always interrupts. Listening re-opens only after an echo tail so the AI never hears itself.
  const play = useCallback((b64, { listenAfter = true, volume = 1 } = {}) => new Promise((resolve) => {
    const after = () => { if (listenAfter) setTimeout(() => listen().catch(() => {}), ECHO_TAIL_MS); };
    if (!b64) { after(); resolve("none"); return; }
    const a = new Audio(`data:audio/mp3;base64,${b64}`);
    a.volume = Math.max(0, Math.min(1, volume));
    r.current.audio = a;
    r.current.resolvePlay = resolve;
    let loudMs = 0;
    let last = Date.now();
    const done = (why) => {
      if (r.current.audio !== a) return;
      r.current.audio = null; stopLoop(); setState("idle");
      if (why === "barge_in") listen().catch(() => {}); else after();
      resolve(why);
    };
    a.onended = () => done("ended");
    a.onerror = () => done("error");
    setState("speaking");
    a.play().catch(() => done("blocked"));
    if (r.current.an && autoBarge) {
      const tick = () => {
        const now = Date.now();
        loudMs = isVoice(rms(), 5) ? loudMs + (now - last) : 0;
        last = now;
        if (loudMs > 600) { a.pause(); done("barge_in"); return; }
        r.current.raf = requestAnimationFrame(tick);
      };
      r.current.raf = requestAnimationFrame(tick);
    }
  }), [listen, autoBarge]); // eslint-disable-line react-hooks/exhaustive-deps

  const release = useCallback(() => {
    interrupt();
    stopListening(false);
    r.current.stream?.getTracks().forEach((t) => t.stop());
    r.current.ctx?.close?.();
    r.current = { floor: 0.01 };
  }, [interrupt, stopListening]);

  const setOnNoSpeech = useCallback((fn) => { r.current.onNoSpeech = fn; }, []);
  useEffect(() => () => release(), [release]);
  return { engine: VOICE_ENGINE, supported, state, level, listen, stopListening, interrupt, play, ensureMic, release, micError, applied, setOnNoSpeech };
}
